import type {
  DataSource,
  DataTable,
  DataTableReader,
  DataValue as TableauDataValue,
  Encoding,
  Environment,
  Filter,
  TableauEvent,
  VisualSpecification,
  Worksheet,
  Column,
} from "@tableau/extensions-api-types";

type DataValue = TableauDataValue & {
  originalFieldName: string;
};

export type VizDataRecord = Record<string, DataValue | DataValue[]>;

export class TableauVizService {
  watchUnderlyingData(handler: () => void) {
    this.addEventListener(tableau.TableauEventType.FilterChanged, handler);
  }

  public async getFilters(): Promise<Filter[]> {
    if (!tableau.extensions.worksheetContent?.worksheet) {
      throw new Error("No worksheet found");
    }

    return tableau.extensions.worksheetContent.worksheet.getFiltersAsync();
  }

  async loadUnderlyingVizData(
    dataSourceName: string,
    options?: { pagination?: { pageRowCount: number; page: number } },
  ): Promise<Record<string, TableauDataValue>[]> {
    const logicalTable =
      await this.getLogicalTableFromDataSource(dataSourceName);

    const dataTableReader =
      await tableau.extensions.worksheetContent!.worksheet.getUnderlyingTableDataReaderAsync(
        logicalTable.id,
        options?.pagination?.pageRowCount,
        {
          includeAllColumns: true,
          includeDataValuesOption:
            tableau.IncludeDataValuesOption.OnlyNativeValues,
        },
      );
    const dataTable = await this.fetchPaginatedData(dataTableReader, options);
    await dataTableReader.releaseAsync();

    return this.transformDataTableToRecords(dataTable);
  }

  private async getLogicalTableFromDataSource(dataSourceName: string) {
    const dataSource = await this.getDataSourceByName(dataSourceName);
    const logicalTable = await this.getFirstLogicalTable(dataSource);
    return logicalTable;
  }

  private transformDataTableToRecords(
    dataTable: DataTable,
  ): Record<string, TableauDataValue>[] {
    return dataTable.data.map((row) =>
      this.transformRowToRecord(row, dataTable),
    );
  }

  private transformRowToRecord(
    row: TableauDataValue[],
    dataTable: DataTable,
  ): Record<string, TableauDataValue> {
    return row.reduce<Record<string, TableauDataValue>>((acc, value, index) => {
      const fieldByIndex = {
        [dataTable.columns[index].fieldName]: value,
      };
      return { ...acc, ...fieldByIndex };
    }, {});
  }

  private async fetchPaginatedData(
    dataTableReader: DataTableReader,
    options?: { pagination?: { pageRowCount: number; page: number } },
  ) {
    return options?.pagination
      ? await dataTableReader.getPageAsync(options.pagination.page)
      : await dataTableReader.getAllPagesAsync();
  }

  private async getFirstLogicalTable(dataSource: DataSource) {
    const logicalTables = await dataSource.getLogicalTablesAsync();
    const logicalTable = logicalTables[0];
    return logicalTable;
  }

  private async getDataSourceByName(dataSourceName: string) {
    const dataSources =
      await tableau.extensions.worksheetContent?.worksheet.getDataSourcesAsync();
    const dataSource = dataSources?.find(
      (dataSource) => dataSource.name === dataSourceName,
    );
    if (!dataSource) {
      throw new Error(`No data source found with name ${dataSourceName}`);
    }
    return dataSource;
  }

  public get isEditorMode(): boolean {
    return tableau.extensions.environment.mode === "authoring";
  }
  private get worksheet(): Worksheet | undefined {
    return tableau.extensions.worksheetContent?.worksheet;
  }

  get environment(): Environment {
    return tableau.extensions.environment;
  }

  public addEventListener(
    event: TableauEvent["type"],
    handler: () => unknown,
  ): { clear: () => void } {
    if (!tableau.extensions.worksheetContent?.worksheet) {
      throw new Error("No worksheet found");
    }

    const clear =
      tableau.extensions.worksheetContent?.worksheet.addEventListener(
        event,
        handler,
      );
    return { clear };
  }

  public watchSummaryData(handler: () => unknown) {
    this.addEventListener(tableau.TableauEventType.SummaryDataChanged, handler);
  }

  private async getSummaryDataTableFromWorksheet(options?: {
    pagination?: { pageRowCount: number; page: number };
  }): Promise<DataTable | null> {
    const dataTableReader =
      await tableau.extensions.worksheetContent!.worksheet.getSummaryDataReaderAsync(
        options?.pagination?.pageRowCount,
      );
    if (dataTableReader.totalRowCount === 0) {
      return null;
    }
    const dataTable =
      options?.pagination === undefined
        ? await dataTableReader.getAllPagesAsync()
        : await dataTableReader.getPageAsync(options.pagination.page);
    await dataTableReader.releaseAsync();
    return dataTable;
  }

  public async loadSummaryVizData(options?: {
    pagination?: { pageRowCount: number; page: number };
  }): Promise<{
    data: VizDataRecord[];
    activeEncodings: Encoding[];
  }> {
    if (!this.worksheet) {
      throw new Error("No worksheet found");
    }
    const dataTable = await this.getSummaryDataTableFromWorksheet(options);
    const visualSpecification = await this.findVisualSpecification();
    const encodings =
      this.getEncodingsFromVisualSpecification(visualSpecification);
    if (!dataTable) {
      return {
        data: [],
        activeEncodings: encodings,
      };
    }
    const data = this.generateRecordsFromDataTable(
      dataTable,
      visualSpecification,
    );

    // Reversing the data here because Tableau returns the data in reverse order too.
    const sortedData = [...data].reverse();
    return {
      data: sortedData,
      activeEncodings: encodings,
    };
  }

  public async findVisualSpecification(): Promise<VisualSpecification> {
    if (!this.worksheet) {
      throw new Error("No worksheet found");
    }
    return this.worksheet.getVisualSpecificationAsync();
  }

  private generateRecordsFromDataTable(
    dataTable: DataTable,
    visualSpecification: VisualSpecification,
  ): VizDataRecord[] {
    return dataTable.data.reduce<VizDataRecord[]>((dataRecords, row) => {
      const dataFields = this.extractDataFieldsFromRow(
        row,
        dataTable,
        visualSpecification,
      );

      dataRecords.push(dataFields);
      return dataRecords;
    }, []);
  }

  private getEncodingsFromVisualSpecification(
    visualSpecification: VisualSpecification,
  ): Encoding[] {
    return visualSpecification.marksSpecifications[
      visualSpecification.activeMarksSpecificationIndex
    ].encodings;
  }

  private extractDataFieldsFromRow(
    row: TableauDataValue[],
    dataTable: DataTable,
    visualSpecification: VisualSpecification,
  ) {
    const activeEncodings =
      this.getEncodingsFromVisualSpecification(visualSpecification);
    return activeEncodings.reduce<VizDataRecord>((acc, encoding) => {
      const dataTableColumn = dataTable.columns.find(
        (column) => column.fieldId === encoding.field.fieldId,
      );
      if (!dataTableColumn) {
        throw new Error(`No column found for field ${encoding.field.name}`);
      }
      const currentValueForEncoding = acc[encoding.id];
      if (Array.isArray(currentValueForEncoding)) {
        // If already an array, append the new value
        return {
          ...acc,
          [encoding.id]: [
            ...currentValueForEncoding,
            this.mapRowToDataValue(row, dataTableColumn),
          ],
        };
      } else if (currentValueForEncoding !== undefined) {
        // If a single value exists, convert to array
        return {
          ...acc,
          [encoding.id]: [
            currentValueForEncoding,
            this.mapRowToDataValue(row, dataTableColumn),
          ],
        };
      }
      // First value for this encoding
      return {
        ...acc,
        [encoding.id]: this.mapRowToDataValue(row, dataTableColumn),
      };
    }, {});
  }

  private mapRowToDataValue(
    row: TableauDataValue[],
    dataTableColumn: Column,
  ): DataValue {
    return {
      formattedValue: row[dataTableColumn.index].formattedValue,
      nativeValue: row[dataTableColumn.index].nativeValue,
      value: row[dataTableColumn.index].value,
      originalFieldName: dataTableColumn.fieldName,
    };
  }
}

export const tableauVizService = new TableauVizService();
